/**
 * MasonryGrid component renders a responsive masonry-style grid layout.
 * It dynamically adjusts the number of columns (1, 2, or 3) based on the current Tailwind CSS breakpoints.
 * To avoid layout shifts, it renders a CSS-only version of the grid on the first-render, then switches to a JavaScript-based layout for better (react) performance.
 */
import type { ReactNode } from 'react'
import { Children, useMemo } from 'react'

import { useTailwindBreakpoint } from '~/hooks/useTailwindBreakpoint'
import { cn } from '~/utils/classnames'

interface MasonryGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: 2 | 3
}

const MasonryGrid = ({
  className,
  columns = 3,
  ...props
}: MasonryGridProps) => {
  const isMd = useTailwindBreakpoint('md')

  if (columns === 2) {
    return (
      <MasonryLayout
        columns={isMd ? 2 : 1}
        className={cn(className)}
        {...props}
      />
    )
  }

  return (
    <MasonryLayout
      columns={isMd ? 3 : 2}
      className={cn(className)}
      {...props}
    />
  )
}

interface MasonryLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: number
}

const MasonryLayout = ({
  children,
  className,
  columns: numColumns,
  ...props
}: MasonryLayoutProps) => {
  const columns = useMemo(() => {
    const columnsCount = numColumns ?? 1

    return Children.toArray(children).reduce<ReactNode[][]>(
      (acc, child, index) => {
        const i = index % columnsCount

        if (acc[i]) {
          acc[i]?.push(child)
        } else {
          acc[i] = [child]
        }

        return acc
      },
      []
    )
  }, [children, numColumns])

  return (
    <div
      className={cn('grid w-full gap-4 @md:gap-6', className)}
      style={{ gridTemplateColumns: `repeat(${numColumns}, minmax(0,1fr))` }}
      {...props}
    >
      {columns.map((colChildren, index) => (
        <div key={index} className="flex flex-col gap-8">
          {colChildren}
        </div>
      ))}
    </div>
  )
}

export { MasonryGrid, MasonryLayout }
